import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    BarChart3, FolderOpen, CheckCircle2, AlertTriangle, Calendar,
    Wrench, UserX, TrendingUp, ChevronRight, Loader2, ArrowUpRight,
    FileWarning, RefreshCcw, PieChart as PieIcon,
} from "lucide-react";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip as RTooltip, Legend, PieChart, Pie, Cell,
} from "recharts";

import OperationsShell from "@/components/operations/OperationsShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import PortalHero from "@/components/ui/PortalHero";
import { portalIdentity } from "@/lib/portalIdentity";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { homeForRole } from "@/components/ProtectedRoute";

/**
 * OPS-001 · Operations Reporting & Control Hub.
 *
 * Replaces the previous "Reports — module under construction" placeholder.
 * All metrics are aggregated server-side by `GET /api/operations/reports/summary`
 * using the SAME canonical statuses, workload rules and overdue semantics
 * already used by the Ops Dashboard, Projects, Calendar and Engineers pages.
 *
 * The hub is drill-through wherever a meaningful existing destination
 * exists (Projects list, Engineers admin, Calendar). Metrics for which
 * no dedicated Ops target yet exists route to the closest existing
 * project-scoped destination — OPS-002 will add richer saved queues.
 */

const PERIOD_OPTIONS = [
    { key: "7d",  label: "Last 7 days" },
    { key: "30d", label: "Last 30 days" },
    { key: "90d", label: "Last 90 days" },
    { key: "mtd", label: "This month" },
    { key: "ytd", label: "This year" },
];

const STATE_LABELS = {
    active:    "Active",
    on_hold:   "On hold",
    completed: "Completed",
};

const STATE_COLOURS = {
    active:    "#3E9E2E",
    on_hold:   "#B45309",
    completed: "#4B5565",
};

// OPS-003A · Reports Hub exceptions now consume the canonical DEV-016
// engine directly. Kept the amber/neutral tone map keyed on severity.
const SEV_TONE = {
    critical: { chip: "bg-[#FEE2E2] text-[#B91C1C]", label: "Critical" },
    high:     { chip: "bg-[#FEF3C7] text-[#B45309]", label: "High" },
    medium:   { chip: "bg-[#FEF9C3] text-[#854D0E]", label: "Medium" },
    low:      { chip: "bg-[#E0F2FE] text-[#0369A1]", label: "Low" },
    information: { chip: "bg-[#E5E7EB] text-[#131B2F]", label: "Info" },
};

export default function OperationsReports() {
    const navigate = useNavigate();
    const { user } = useAuth();
    // SALES-ROLE-001B · Role-aware "Back" target on the forbidden
    // panel — Sales returns to /sales, Operations to /operations.
    // Never falls back to a role-guarded route that would loop.
    const homeHref = homeForRole(user?.role) || "/";
    const homeLabel = user?.role === "sales" ? "Back to Sales dashboard" : "Back to Operations";
    const [period, setPeriod] = useState("30d");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // OPS-003A · Reports Hub reads canonical DEV-016 exceptions in
    // addition to the legacy reports/summary payload.
    const [exceptionsData, setExceptionsData] = useState(null);
    const [exceptionsLoading, setExceptionsLoading] = useState(true);
    const [exceptionsError, setExceptionsError] = useState(null);

    const load = useCallback(async (p) => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.operationsReportsSummary(p);
            setData(res);
        } catch (e) {
            const msg = formatApiError(e?.response?.data?.detail) || "Failed to load reports";
            setError(msg);
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadExceptions = useCallback(async () => {
        setExceptionsLoading(true);
        setExceptionsError(null);
        try {
            const [sum, list] = await Promise.all([
                api.operationsExceptionsSummary(),
                api.operationsExceptionsSearch({
                    status: "active",
                    sort: "severity_desc",
                    limit: 20,
                    offset: 0,
                }),
            ]);
            setExceptionsData({ summary: sum, list });
        } catch (e) {
            setExceptionsError(formatApiError(e?.response?.data?.detail) || "Failed to load exceptions");
        } finally {
            setExceptionsLoading(false);
        }
    }, []);

    useEffect(() => { load(period); }, [load, period]);
    useEffect(() => { loadExceptions(); }, [loadExceptions]);

    return (
        <OperationsShell>
            <PageContainer width="full" testid="ops-reports-page">
                {/* DEV-022 · Reports premium hero — surfaces the
                    headline programme fact (active projects +
                    exceptions) above the tactical KPI grid. */}
                {(() => {
                    const identity = portalIdentity(user?.role);
                    const s = data?.summary || {};
                    const active = s.active_projects;
                    const completedInPeriod = s.projects_completed_in_period;
                    const overdue = s.overdue_projects || 0;
                    const sentence = loading ? (
                        <>Loading reporting summary…</>
                    ) : error ? (
                        <>Reports & Control Hub summary is temporarily unavailable.</>
                    ) : (
                        <>
                            <strong className="text-white font-semibold">{active ?? "—"}</strong> active {active === 1 ? "project" : "projects"} ·{" "}
                            <strong className="text-white font-semibold">{completedInPeriod ?? "—"}</strong> completed in {data?.period?.label || "period"}
                            {overdue > 0 && <> · <span className="text-[#FCD34D]">{overdue} overdue</span></>}
                        </>
                    );
                    return (
                        <div className="mb-6">
                            <PortalHero
                                testid="ops-reports-hero"
                                eyebrow={`${identity.eyebrow} · REPORTS`}
                                title="Reports & Control Hub"
                                sentence={sentence}
                                cta={{
                                    to: "/operations",
                                    label: "Back to Operations",
                                    icon: BarChart3,
                                }}
                                ring={{
                                    value: loading ? "—" : (active ?? 0),
                                    label: "active projects",
                                }}
                                tag={data?.period?.label || "Live programme data"}
                            />
                        </div>
                    );
                })()}
                <PageHeader
                    eyebrow="OPERATIONS"
                    title="Reports & Control Hub"
                    subtitle="Fleet-wide KPIs, exceptions and workload — sourced from the canonical Projects, Tasks, Calendar and Site Survey data."
                    icon={BarChart3}
                    backFallback="/operations"
                    testid="ops-reports"
                    rightSlot={<PeriodSelector value={period} onChange={setPeriod} disabled={loading} />}
                    className="sr-only"
                />

                <div className="flex items-center justify-between gap-4 mt-2">
                    <div className="flex-1 min-w-0">
                        <PeriodSelector value={period} onChange={setPeriod} disabled={loading} />
                    </div>
                </div>
                {error && (
                    <div className="mb-6 rounded-lg border border-[#F59E0B]/50 bg-[#FEF3C7]/40 px-4 py-3 flex items-center gap-3" data-testid="ops-reports-error">
                        <AlertTriangle size={16} className="text-[#B45309]" />
                        <div className="text-sm text-[#131B2F] flex-1">{error}</div>
                        <button
                            onClick={() => load(period)}
                            data-testid="ops-reports-retry"
                            className="text-xs font-medium text-[#131B2F] hover:underline flex items-center gap-1"
                        >
                            <RefreshCcw size={12} /> Retry
                        </button>
                    </div>
                )}

                {/* SALES-ROLE-001B · When the summary endpoint is
                    forbidden or fails we MUST NOT render the KPI /
                    lifecycle / throughput cards — those would emit
                    fabricated "0" values and the "No projects on
                    file yet" empty statement, which the Sales UAT
                    correctly called out as misleading. Falling back
                    to a clear denied-state panel keeps navigation
                    working (Back to Operations) without inventing
                    data. */}
                {error ? (
                    <div
                        className="rounded-xl border border-[#E5E7EB] bg-white p-8 text-center"
                        data-testid="ops-reports-denied"
                    >
                        <AlertTriangle size={20} className="mx-auto text-[#B45309]" />
                        <div className="mt-3 text-sm font-medium text-[#131B2F]">
                            Reports & Control Hub is not available for your account
                        </div>
                        <div className="mt-1 text-xs text-[#4B5565]">
                            {error}
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate(homeHref)}
                            data-testid="ops-reports-denied-back"
                            className="mt-4 inline-flex items-center h-9 px-3 rounded-md bg-[#131B2F] text-white text-sm hover:brightness-110"
                        >
                            {homeLabel}
                        </button>
                    </div>
                ) : (
                <>
                {/* KPI cards */}
                <KpiRow data={data} loading={loading} navigate={navigate} />

                {/* Lifecycle + Throughput */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mt-6">
                    <div className="lg:col-span-2">
                        <LifecycleCard data={data} loading={loading} navigate={navigate} />
                    </div>
                    <div>
                        <ThroughputCard data={data} loading={loading} periodLabel={PERIOD_OPTIONS.find((p) => p.key === period)?.label} />
                    </div>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mt-6">
                    <TrendCard
                        title="Projects created vs. completed"
                        subtitle="Rolling activity across the reporting period."
                        data={data?.trends?.projects_over_time || []}
                        lines={[
                            { key: "created",   label: "Created",   colour: "#3E9E2E" },
                            { key: "completed", label: "Completed", colour: "#131B2F" },
                        ]}
                        loading={loading}
                        testid="ops-reports-trend-projects"
                    />
                    <TrendCard
                        title="Site Surveys submitted vs. approved"
                        subtitle="Site Survey lifecycle events across the reporting period."
                        data={data?.trends?.surveys_over_time || []}
                        lines={[
                            { key: "submitted", label: "Submitted", colour: "#F59E0B" },
                            { key: "approved",  label: "Approved",  colour: "#3E9E2E" },
                        ]}
                        loading={loading}
                        testid="ops-reports-trend-surveys"
                    />
                </div>

                {/* Upcoming operational work */}
                <div className="mt-6">
                    <UpcomingWorkCard rows={data?.upcoming_work || []} loading={loading} navigate={navigate} />
                </div>

                {/* Engineer workload + Exceptions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mt-6">
                    <EngineerWorkloadCard rows={data?.engineer_workload || []} loading={loading} navigate={navigate} />
                    <ExceptionsCard
                        exceptionsData={exceptionsData}
                        loading={exceptionsLoading}
                        error={exceptionsError}
                        onRetry={loadExceptions}
                        navigate={navigate}
                    />
                </div>
                </>
                )}
            </PageContainer>
        </OperationsShell>
    );
}

/* ─── Period selector ─────────────────────────────────────────────────── */

function PeriodSelector({ value, onChange, disabled }) {
    return (
        <div className="flex items-center gap-2" data-testid="ops-reports-period">
            <label htmlFor="ops-reports-period-select" className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">
                Period
            </label>
            <select
                id="ops-reports-period-select"
                data-testid="ops-reports-period-select"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                className="h-9 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm text-[#131B2F] focus:outline-none focus:ring-2 focus:ring-[#131B2F] disabled:opacity-50"
            >
                {PERIOD_OPTIONS.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                ))}
            </select>
        </div>
    );
}

/* ─── KPI row ─────────────────────────────────────────────────────────── */

function KpiRow({ data, loading, navigate }) {
    const s = data?.summary || {};
    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4" data-testid="ops-reports-kpis">
            <Kpi
                testid="ops-reports-kpi-active-projects"
                label="Active projects"
                sublabel="Current"
                value={s.active_projects}
                icon={FolderOpen}
                loading={loading}
                onClick={() => navigate("/operations/projects?state=active")}
            />
            <Kpi
                testid="ops-reports-kpi-projects-completed"
                label="Projects completed"
                sublabel={data?.period?.label || "In period"}
                value={s.projects_completed_in_period}
                icon={CheckCircle2}
                loading={loading}
                onClick={() => navigate("/operations/projects?state=completed")}
            />
            <Kpi
                testid="ops-reports-kpi-overdue-tasks"
                label="Overdue projects"
                sublabel="Current"
                value={s.overdue_projects}
                icon={AlertTriangle}
                tone={s.overdue_projects > 0 ? "amber" : "neutral"}
                loading={loading}
                onClick={() => navigate("/operations/projects?queue=overdue")}
            />
            <Kpi
                testid="ops-reports-kpi-upcoming-surveys"
                label="Upcoming Site Surveys"
                sublabel="Next 14 days"
                value={s.upcoming_surveys}
                icon={Calendar}
                loading={loading}
                onClick={() => navigate("/operations/projects?queue=survey_scheduled&window=14d")}
            />
            <Kpi
                testid="ops-reports-kpi-upcoming-installs"
                label="Upcoming installations"
                sublabel="Next 14 days"
                value={s.upcoming_installations}
                icon={Calendar}
                loading={loading}
                onClick={() => navigate("/operations/projects?queue=installation_this_week&window=14d")}
            />
            <Kpi
                testid="ops-reports-kpi-unassigned"
                label="Unassigned work"
                sublabel="Current"
                value={s.unassigned_projects}
                icon={UserX}
                tone={s.unassigned_projects > 0 ? "amber" : "neutral"}
                loading={loading}
                onClick={() => navigate("/operations/projects?queue=no_engineer_assigned")}
            />
            <Kpi
                testid="ops-reports-kpi-workload"
                label="Engineers with load"
                sublabel={`${s.engineer_workload?.total_engineers ?? 0} total`}
                value={s.engineer_workload?.engineers_with_load}
                icon={Wrench}
                loading={loading}
                onClick={() => navigate("/operations/engineers")}
            />
        </div>
    );
}

function Kpi({ testid, label, sublabel, value, icon: Icon, tone = "neutral", loading, onClick }) {
    const cls = tone === "amber"
        ? "border-[#F59E0B]/40 bg-[#FEF3C7]/40 hover:bg-[#FEF3C7]/60"
        : "border-[#E5E7EB] bg-white hover:bg-[#F9FAFB]";
    const body = (
        <>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.12em] text-[#4B5565] font-medium">{label}</div>
                <Icon size={14} strokeWidth={1.75} className={tone === "amber" ? "text-[#B45309]" : "text-[#4B5565]"} />
            </div>
            <div className={`mt-2 text-3xl font-semibold ${tone === "amber" ? "text-[#B45309]" : "text-[#131B2F]"}`}>
                {loading ? "—" : (value ?? 0)}
            </div>
            {sublabel && <div className="mt-0.5 text-[11px] text-[#4B5565]">{sublabel}</div>}
        </>
    );
    if (!onClick) return <div className={`rounded-xl border ${cls} p-4`} data-testid={testid}>{body}</div>;
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-xl border ${cls} p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#131B2F]`}
            data-testid={testid}
        >
            {body}
        </button>
    );
}

/* ─── Lifecycle distribution ─────────────────────────────────────────── */

function LifecycleCard({ data, loading, navigate }) {
    const rows = useMemo(() => data?.lifecycle || [], [data]);
    const total = rows.reduce((sum, r) => sum + (r.count || 0), 0);
    const pieData = useMemo(
        () => rows.map((r) => ({
            name: STATE_LABELS[r.state] || r.state,
            value: r.count,
            state: r.state,
        })),
        [rows],
    );
    return (
        <Card testid="ops-reports-lifecycle" title="Project lifecycle distribution" icon={PieIcon}
              subtitle="Every project across the fleet — grouped by canonical state.">
            {loading ? <LoadingBlock /> : total === 0 ? (
                <EmptyState message="No projects on file yet." />
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="h-52" data-testid="ops-reports-lifecycle-chart">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    innerRadius={45}
                                    outerRadius={80}
                                    dataKey="value"
                                    nameKey="name"
                                    isAnimationActive={false}
                                >
                                    {pieData.map((entry, i) => (
                                        <Cell
                                            key={i}
                                            fill={STATE_COLOURS[entry.state] || "#94A3B8"}
                                            stroke="#fff"
                                        />
                                    ))}
                                </Pie>
                                <RTooltip formatter={(v, n) => [v, n]} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <ul className="flex flex-col justify-center gap-2" data-testid="ops-reports-lifecycle-list">
                        {rows.map((r) => (
                            <li key={r.state}>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/operations/projects?state=${encodeURIComponent(r.state)}`)}
                                    data-testid={`ops-reports-lifecycle-row-${r.state}`}
                                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[#F9FAFB] transition-colors text-left"
                                >
                                    <span
                                        className="w-2.5 h-2.5 rounded-full shrink-0"
                                        style={{ background: STATE_COLOURS[r.state] || "#94A3B8" }}
                                    />
                                    <span className="flex-1 text-sm text-[#131B2F]">{STATE_LABELS[r.state] || r.state}</span>
                                    <span className="text-sm font-medium text-[#131B2F]">{r.count}</span>
                                    <span className="text-[11px] text-[#4B5565] tabular-nums w-10 text-right">{r.percentage}%</span>
                                    <ChevronRight size={12} className="text-[#4B5565]" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Card>
    );
}

/* ─── Throughput ─────────────────────────────────────────────────────── */

function ThroughputCard({ data, loading, periodLabel }) {
    const t = data?.throughput || {};
    const items = [
        { key: "projects_created",   label: "Projects created" },
        { key: "projects_completed", label: "Projects completed" },
        { key: "surveys_submitted",  label: "Site Surveys submitted" },
        { key: "surveys_approved",   label: "Site Surveys approved" },
        { key: "visits_completed",   label: "Visits completed" },
    ];
    return (
        <Card testid="ops-reports-throughput" title="Throughput" icon={TrendingUp}
              subtitle={periodLabel || "Reporting period."}>
            {loading ? <LoadingBlock /> : (
                <ul className="divide-y divide-[#F1F5F9]" data-testid="ops-reports-throughput-list">
                    {items.map((it) => (
                        <li key={it.key} className="flex items-center justify-between py-2.5">
                            <span className="text-sm text-[#131B2F]">{it.label}</span>
                            <span
                                className="text-sm font-semibold text-[#131B2F] tabular-nums"
                                data-testid={`ops-reports-throughput-${it.key}`}
                            >
                                {t[it.key] ?? 0}
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}

/* ─── Trend charts ───────────────────────────────────────────────────── */

function TrendCard({ title, subtitle, data, lines, loading, testid }) {
    const empty = !loading && (!data || data.length === 0);
    return (
        <Card testid={testid} title={title} icon={TrendingUp} subtitle={subtitle}>
            {loading ? <LoadingBlock /> : empty ? (
                <EmptyState message="No activity in the selected period." />
            ) : (
                <div className="h-64" data-testid={`${testid}-chart`}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11, fill: "#4B5565" }}
                                tickFormatter={_fmtShortDate}
                            />
                            <YAxis
                                allowDecimals={false}
                                tick={{ fontSize: 11, fill: "#4B5565" }}
                                width={28}
                            />
                            <RTooltip
                                labelFormatter={_fmtShortDate}
                                contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#E5E7EB" }}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {lines.map((ln) => (
                                <Line
                                    key={ln.key}
                                    type="monotone"
                                    dataKey={ln.key}
                                    name={ln.label}
                                    stroke={ln.colour}
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </Card>
    );
}

/* ─── Upcoming work ──────────────────────────────────────────────────── */

function UpcomingWorkCard({ rows, loading, navigate }) {
    return (
        <Card
            testid="ops-reports-upcoming"
            title="Upcoming operational work"
            icon={Calendar}
            subtitle="Next 14 days — Site Surveys, installations, commissioning and handovers. Duplicating the full Calendar is not the goal here."
            rightSlot={
                <Link
                    to="/operations/calendar"
                    data-testid="ops-reports-open-calendar"
                    className="inline-flex items-center gap-1.5 text-xs text-[#131B2F] hover:underline"
                >
                    <ArrowUpRight size={12} /> Open calendar
                </Link>
            }
        >
            {loading ? <LoadingBlock /> : rows.length === 0 ? (
                <EmptyState message="No scheduled operational work in the next 14 days." />
            ) : (
                <div className="overflow-x-auto" data-testid="ops-reports-upcoming-table">
                    <table className="min-w-full text-sm">
                        <thead className="text-[11px] uppercase tracking-[0.1em] text-[#4B5565]">
                            <tr>
                                <th className="text-left font-medium py-2 pr-3">When</th>
                                <th className="text-left font-medium py-2 pr-3">Type</th>
                                <th className="text-left font-medium py-2 pr-3">Customer</th>
                                <th className="text-left font-medium py-2 pr-3">Engineer</th>
                                <th className="text-left font-medium py-2 pr-3">Status</th>
                                <th className="text-right font-medium py-2 pl-3">Open</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F1F5F9]">
                            {rows.map((r) => (
                                <tr key={r.appointment_id} className="hover:bg-[#F9FAFB]" data-testid={`ops-reports-upcoming-row-${r.appointment_id}`}>
                                    <td className="py-2.5 pr-3 text-[#131B2F]">{_fmtDateTime(r.start_at)}</td>
                                    <td className="py-2.5 pr-3 text-[#131B2F]">{r.type || r.title || "—"}</td>
                                    <td className="py-2.5 pr-3 text-[#131B2F]">{r.customer_name || "—"}</td>
                                    <td className="py-2.5 pr-3 text-[#4B5565]">{r.engineer_name || "Unassigned"}</td>
                                    <td className="py-2.5 pr-3"><StatusChip status={r.status} /></td>
                                    <td className="py-2.5 pl-3 text-right">
                                        <Link
                                            to={`/operations/projects/${r.project_id}`}
                                            className="inline-flex items-center gap-1 text-xs text-[#131B2F] hover:underline"
                                            data-testid={`ops-reports-upcoming-open-${r.appointment_id}`}
                                        >
                                            Project <ArrowUpRight size={12} />
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}

/* ─── Engineer workload ──────────────────────────────────────────────── */

function EngineerWorkloadCard({ rows, loading, navigate }) {
    return (
        <Card
            testid="ops-reports-engineer-workload"
            title="Engineer workload overview"
            icon={Wrench}
            subtitle="Fleet-wide task and visit load — same rules as the Engineers admin page."
            rightSlot={
                <Link
                    to="/operations/engineers"
                    data-testid="ops-reports-open-engineers"
                    className="inline-flex items-center gap-1.5 text-xs text-[#131B2F] hover:underline"
                >
                    <ArrowUpRight size={12} /> Manage engineers
                </Link>
            }
        >
            {loading ? <LoadingBlock /> : rows.length === 0 ? (
                <EmptyState message="No active engineers on the roster." />
            ) : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm" data-testid="ops-reports-engineer-table">
                        <thead className="text-[11px] uppercase tracking-[0.1em] text-[#4B5565]">
                            <tr>
                                <th className="text-left font-medium py-2 pr-3">Engineer</th>
                                <th className="text-right font-medium py-2 pr-3">Projects</th>
                                <th className="text-right font-medium py-2 pr-3">Open tasks</th>
                                <th className="text-right font-medium py-2 pr-3">Overdue</th>
                                <th className="text-right font-medium py-2 pr-3">Visits (wk)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F1F5F9]">
                            {rows.slice(0, 12).map((r) => (
                                <tr
                                    key={r.engineer_id}
                                    className="hover:bg-[#F9FAFB] cursor-pointer"
                                    data-testid={`ops-reports-engineer-row-${r.engineer_id}`}
                                    onClick={() => navigate("/operations/engineers")}
                                >
                                    <td className="py-2.5 pr-3">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="w-2 h-2 rounded-full shrink-0"
                                                style={{ background: r.colour || "#94A3B8" }}
                                            />
                                            <span className="text-[#131B2F]">{r.display_name || "—"}</span>
                                        </div>
                                    </td>
                                    <td className="py-2.5 pr-3 text-right tabular-nums text-[#131B2F]">{r.assigned_projects}</td>
                                    <td className="py-2.5 pr-3 text-right tabular-nums text-[#131B2F]">{r.open_tasks}</td>
                                    <td className={`py-2.5 pr-3 text-right tabular-nums ${r.overdue_tasks > 0 ? "text-[#B45309] font-medium" : "text-[#4B5565]"}`}>
                                        {r.overdue_tasks}
                                    </td>
                                    <td className="py-2.5 pr-3 text-right tabular-nums text-[#131B2F]">{r.visits_this_week}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
}

/* ─── Exceptions (canonical DEV-016) ─────────────────────────────── */

function ExceptionsCard({ exceptionsData, loading, error, onRetry, navigate }) {
    const rows = exceptionsData?.list?.exceptions || [];
    const total = exceptionsData?.list?.total ?? rows.length;
    const summary = exceptionsData?.summary;
    return (
        <Card
            testid="ops-reports-exceptions"
            title="Operational exceptions"
            icon={FileWarning}
            subtitle="Live from the canonical DEV-016 rules engine — dedup, severity ageing, resolution history."
            rightSlot={
                <Link
                    to="/operations/attention"
                    data-testid="ops-reports-open-attention"
                    className="inline-flex items-center gap-1.5 text-xs text-[#131B2F] hover:underline"
                >
                    <ArrowUpRight size={12} /> Open Attention Centre
                </Link>
            }
        >
            {error ? (
                <div className="py-3 text-sm text-[#B91C1C] flex items-center gap-3" data-testid="ops-reports-exceptions-error">
                    <span className="flex-1">{error}</span>
                    <button onClick={onRetry} className="text-xs underline">Retry</button>
                </div>
            ) : loading ? <LoadingBlock /> : (
                <>
                    {summary && (
                        <div className="mb-3 flex flex-wrap gap-2 text-[11px]" data-testid="ops-reports-exceptions-summary">
                            <Chip testid="ops-reports-exceptions-total" tone="neutral">Active {summary.active_total}</Chip>
                            {summary.by_severity?.map((b) => (
                                <Chip key={b.key} testid={`ops-reports-exceptions-sev-${b.key}`} tone={b.key}>
                                    {(SEV_TONE[b.key]?.label || b.key)} {b.count}
                                </Chip>
                            ))}
                        </div>
                    )}
                    {rows.length === 0 ? (
                        <EmptyState message="No active exceptions across the fleet." />
                    ) : (
                        <ul className="divide-y divide-[#F1F5F9]" data-testid="ops-reports-exceptions-list">
                            {rows.map((r) => {
                                const sev = SEV_TONE[r.severity] || SEV_TONE.information;
                                return (
                                    <li key={r.id} data-testid={`ops-reports-exception-row-${r.id}`}>
                                        <button
                                            type="button"
                                            onClick={() => navigate(`/operations/attention?open=${encodeURIComponent(r.id)}`)}
                                            className="w-full py-2.5 flex items-start gap-3 text-left hover:bg-[#F9FAFB] px-2 rounded-md"
                                        >
                                            <span className={`text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${sev.chip}`}>
                                                {sev.label}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-[#131B2F] truncate">
                                                    {r.required_action || r.rule_key}
                                                </div>
                                                <div className="text-[11px] text-[#4B5565] truncate">
                                                    <span>{r.customer_name || "Unknown customer"}</span>
                                                    <span className="mx-1">·</span>
                                                    <span>{r.project_reference || r.project_id?.slice(0, 12)}</span>
                                                    <span className="mx-1">·</span>
                                                    <span>{r.responsible_role || "—"}</span>
                                                </div>
                                            </div>
                                            <ChevronRight size={14} className="text-[#4B5565] shrink-0 mt-1" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {total > rows.length && (
                        <div className="mt-3 text-[11px] text-[#4B5565]" data-testid="ops-reports-exceptions-more">
                            Showing {rows.length} of {total} active exceptions. Open Attention Centre for the full list.
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}

function Chip({ tone, testid, children }) {
    const cls = tone === "critical" ? "bg-[#FEE2E2] text-[#B91C1C]"
        : tone === "high" ? "bg-[#FEF3C7] text-[#B45309]"
        : tone === "medium" ? "bg-[#FEF9C3] text-[#854D0E]"
        : tone === "low" ? "bg-[#E0F2FE] text-[#0369A1]"
        : "bg-[#E5E7EB] text-[#131B2F]";
    return (
        <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${cls}`}>
            {children}
        </span>
    );
}

/* ─── Presentational helpers ─────────────────────────────────────────── */

function Card({ title, subtitle, icon: Icon, testid, rightSlot, children }) {
    return (
        <section className="rounded-xl border border-[#E5E7EB] bg-white" data-testid={testid}>
            <header className="flex items-start gap-3 px-4 md:px-5 py-4 border-b border-[#F1F5F9]">
                {Icon && (
                    <span className="w-9 h-9 rounded-md bg-[#131B2F] text-white flex items-center justify-center shrink-0">
                        <Icon size={16} strokeWidth={1.75} />
                    </span>
                )}
                <div className="flex-1 min-w-0">
                    <h2 className="text-base md:text-lg text-[#131B2F] font-medium">{title}</h2>
                    {subtitle && <p className="text-[11px] md:text-xs text-[#4B5565] mt-0.5">{subtitle}</p>}
                </div>
                {rightSlot}
            </header>
            <div className="p-4 md:p-5">{children}</div>
        </section>
    );
}

function StatusChip({ status }) {
    const label = status ? status.replace(/_/g, " ") : "scheduled";
    return (
        <span
            className={`text-[10px] uppercase tracking-[0.1em] font-medium px-2 py-0.5 rounded-full ${
                status === "completed" ? "bg-[#E1F5E5] text-[#3E9E2E]"
                : status === "cancelled" ? "bg-[#F5F5F7] text-[#4B5565]"
                : "bg-[#E1E6F0] text-[#131B2F]"
            }`}
        >
            {label}
        </span>
    );
}

function LoadingBlock() {
    return (
        <div className="flex items-center gap-2 py-6 text-sm text-[#4B5565]" data-testid="ops-reports-loading">
            <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
    );
}

function EmptyState({ message }) {
    return (
        <div className="py-6 text-sm text-[#4B5565]" data-testid="ops-reports-empty">
            {message}
        </div>
    );
}

function _fmtShortDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    } catch {
        return iso;
    }
}

function _fmtDateTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString(undefined, {
            weekday: "short", day: "numeric", month: "short",
            hour: "2-digit", minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
